<template>
    <div class="step" :class="[layout === 'page' ? 'is-page' : 'is-centred', { 'is-embedded': embedded }]">
        <component :is="tag" v-bind="$attrs" class="column" :class="[WIDTH_CLASSES[width], GAP_CLASSES[gap]]">
            <slot></slot>
        </component>
    </div>
</template>

<script setup lang="ts">
defineOptions({ inheritAttrs: false });

const {
    embedded = false,
    layout = "centred",
    width = "default",
    gap = "default",
    tag = "div",
} = defineProps<{
    /** Hosted inside the guided setup, which supplies its own page frame and measure. */
    embedded?: boolean;
    layout?: "centred" | "page";
    width?: "default" | "narrow" | "wide";
    gap?: "default" | "tight" | "none";
    tag?: "div" | "form";
}>();

const WIDTH_CLASSES = { default: "is-default", narrow: "is-narrow", wide: "is-wide" } as const;
const GAP_CLASSES = { default: "has-gap", tight: "has-tight-gap", none: "" } as const;
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /*
     * With auto vertical margins, 28vh bottom padding shifts the column upward by 14vh while spare space
     * remains.
     */
    .step.is-centred {
        @apply flex flex-1 justify-center px-6 pt-6 pb-[28vh];
    }

    .step.is-centred.is-embedded {
        @apply block p-0;
    }

    .step.is-page {
        @apply px-6 py-6 md:py-10;
    }

    .step.is-page.is-embedded {
        @apply p-0;
    }

    .column {
        @apply flex w-full flex-col;
    }

    /*
     * Use auto margins instead of alignment centering so overflowing content remains reachable at the top.
     */
    .is-centred > .column {
        @apply my-auto;
    }

    .is-page > .column {
        @apply @container;
        @apply mx-auto;
    }

    .is-page.is-embedded > .column {
        @apply mx-0;
    }

    .column.is-default {
        @apply max-w-168;
    }

    .column.is-narrow {
        @apply max-w-140;
    }

    .column.is-wide {
        @apply max-w-4xl;
    }

    /*
     * The host page controls embedded content width; applying another maximum here would conflict with it.
     */
    .is-embedded > .column {
        @apply max-w-none;
    }

    .column.has-gap {
        @apply gap-6;
    }

    .column.has-tight-gap {
        @apply gap-5;
    }
}
</style>
